// src/pages/GameTargetPractice.jsx
// Piano With Pan — Target Practice (Game Mode)
// Shoot falling targets with piano notes — timing-based gameplay

import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import RealPianoListener from "../components/RealPianoListener";
import VirtualPiano, { normalizeNoteName as vpNormalizeNoteName } from "../components/VirtualPiano";

/* ═══════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════ */
const WHITE_NOTES = ["C", "D", "E", "F", "G", "A", "B"];
const TOTAL_TARGETS = 10;
const TARGET_SPAWN_INTERVAL = 5000; // 5 seconds between spawns
const TARGET_FALL_DURATION = 5000;  // 5 seconds to reach bottom
const MAX_HEALTH = 5;
const MIC_ID = "gtp-mic-ctrl";

const NOTE_COL = {
  C: { bg: "#FEE2E2", text: "#991B1B", border: "#FCA5A5", glow: "rgba(239,68,68,0.28)" },
  D: { bg: "#FEF3C7", text: "#92400E", border: "#FCD34D", glow: "rgba(245,158,11,0.28)" },
  E: { bg: "#D1FAE5", text: "#065F46", border: "#6EE7B7", glow: "rgba(52,211,153,0.28)" },
  F: { bg: "#DBEAFE", text: "#1E40AF", border: "#93C5FD", glow: "rgba(96,165,250,0.28)" },
  G: { bg: "#EDE9FE", text: "#5B21B6", border: "#C4B5FD", glow: "rgba(167,139,250,0.28)" },
  A: { bg: "#FCE7F3", text: "#9D174D", border: "#F9A8D4", glow: "rgba(244,114,182,0.28)" },
  B: { bg: "#FFEDD5", text: "#9A3412", border: "#FDBA74", glow: "rgba(251,146,60,0.28)" },
};

const MIC_CFG = {
  detectionSensitivity: 1.25,
  toleranceCents: 85,
  toleranceHz: 14,
  wrongStableFrames: 1,
  wrongCooldownMs: 180,
  wrongCentsThreshold: 70,
  wrongHzThreshold: 7,
};

/* ═══════════════════════════════════════════════
   NOTE HELPERS
═══════════════════════════════════════════════ */
const FLAT_TO_SHARP = { DB: "C#", EB: "D#", GB: "F#", AB: "G#", BB: "A#", CB: "B", FB: "E" };
const EDGE_ENH = { "E#": "F", "B#": "C" };
const MIDI_PC = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

function normSafe(n) {
  if (!n) return "";
  let r = String(n).trim().toUpperCase().replaceAll("♯", "#").replaceAll("♭", "B");
  const m = r.match(/[A-G](#|B)?/);
  if (!m) return "";
  let pc = m[0];
  if (EDGE_ENH[pc]) pc = EDGE_ENH[pc];
  if (pc.length === 2 && pc[1] === "B") pc = FLAT_TO_SHARP[pc] || pc;
  return vpNormalizeNoteName ? vpNormalizeNoteName(pc) : pc;
}

function normAny(x) {
  if (x == null) return "";
  if (Array.isArray(x)) return normAny(x[x.length - 1] ?? x[0]);
  if (typeof x === "number" && Number.isFinite(x)) return normSafe(MIDI_PC[((Math.round(x) % 12) + 12) % 12]);
  if (typeof x === "object" && x.name) return normSafe(x.name);
  return normSafe(String(x).split(/[/,|]/)[0]);
}

/* ═══════════════════════════════════════════════
   GAME STATE HELPERS
═══════════════════════════════════════════════ */
function randomNote() {
  return WHITE_NOTES[Math.floor(Math.random() * WHITE_NOTES.length)];
}

function createTarget(id) {
  return {
    id,
    note: randomNote(),
    spawnTime: Date.now(),
    x: 0.5, // Start at center (0-1 range)
    y: 0,   // Start at top
    active: true,
  };
}

/* ═══════════════════════════════════════════════
   FALLING TARGET COMPONENT
═══════════════════════════════════════════════ */
function FallingTarget({ target, gameAreaWidth, gameAreaHeight }) {
  const elapsed = Date.now() - target.spawnTime;
  const progress = Math.min(elapsed / TARGET_FALL_DURATION, 1);
  
  // Sine wave horizontal movement
  const sineOffset = Math.sin(elapsed / 800) * 0.3; // Oscillate ±30% of width
  const x = Math.max(0.1, Math.min(0.9, target.x + sineOffset)); // Keep in bounds
  
  // Linear vertical fall
  const y = progress;
  
  const col = NOTE_COL[target.note] || NOTE_COL.C;
  const size = 48;
  
  const pixelX = x * gameAreaWidth - size / 2;
  const pixelY = y * gameAreaHeight - size / 2;
  
  return (
    <div
      style={{
        position: "absolute",
        left: pixelX,
        top: pixelY,
        width: size,
        height: size,
        borderRadius: "50%",
        background: col.bg,
        border: `3px solid ${col.border}`,
        boxShadow: `0 4px 16px ${col.glow}, 0 0 0 4px rgba(255,255,255,0.3)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "'Fredoka One', cursive",
        fontSize: "1.2rem",
        color: col.text,
        transition: "transform 0.1s ease",
        pointerEvents: "none",
        zIndex: 10,
      }}
    >
      {target.note}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   PROJECTILE COMPONENT
═══════════════════════════════════════════════ */
function Projectile({ projectile, gameAreaWidth, gameAreaHeight }) {
  const elapsed = Date.now() - projectile.spawnTime;
  const duration = 400; // 400ms to travel up
  const progress = Math.min(elapsed / duration, 1);
  
  // Start from piano position, travel up
  const y = 1 - progress; // 1 (bottom) to 0 (top)
  
  const pixelX = projectile.x * gameAreaWidth - 4;
  const pixelY = y * gameAreaHeight;
  
  const col = NOTE_COL[projectile.note] || NOTE_COL.C;
  
  return (
    <div
      style={{
        position: "absolute",
        left: pixelX,
        top: pixelY,
        width: 8,
        height: 24,
        borderRadius: 4,
        background: `linear-gradient(to top, ${col.border}, ${col.bg})`,
        boxShadow: `0 0 12px ${col.glow}`,
        pointerEvents: "none",
        zIndex: 9,
      }}
    />
  );
}

/* ═══════════════════════════════════════════════
   HEALTH BAR
═══════════════════════════════════════════════ */
function HealthBar({ current, max }) {
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <span style={{ fontSize: "0.7rem", fontWeight: 800, color: "#64748B" }}>HEALTH</span>
      <div style={{ display: "flex", gap: 4 }}>
        {Array.from({ length: max }, (_, i) => (
          <div
            key={i}
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              background: i < current ? "#EF4444" : "#E2E8F0",
              border: `2px solid ${i < current ? "#DC2626" : "#CBD5E1"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.8rem",
              transition: "all 0.3s ease",
            }}
          >
            {i < current ? "❤️" : ""}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   STAT CARD
═══════════════════════════════════════════════ */
function StatCard({ emoji, label, value, color }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.92)",
        borderRadius: 16,
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        boxShadow: "0 3px 14px rgba(0,0,0,0.07)",
      }}
    >
      <span style={{ fontSize: "1.5rem" }}>{emoji}</span>
      <div>
        <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.4rem", color, lineHeight: 1 }}>
          {value}
        </div>
        <div
          style={{
            fontSize: "0.6rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "#94A3B8",
            marginTop: 2,
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   FINISH MODAL
═══════════════════════════════════════════════ */
function FinishModal({ open, magicPoints, hits, misses, onClose, onRetry, goLevels }) {
  if (!open) return null;
  
  const accuracy = hits + misses === 0 ? 100 : Math.round((hits / (hits + misses)) * 100);
  
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: 34,
          padding: "36px 24px 28px",
          maxWidth: 440,
          width: "100%",
          boxShadow: "0 28px 70px rgba(0,0,0,0.26)",
          textAlign: "center",
          position: "relative",
          animation: "gtpPop .4s cubic-bezier(.34,1.56,.64,1)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            position: "absolute",
            top: 14,
            right: 16,
            background: "none",
            border: "none",
            fontSize: "1rem",
            cursor: "pointer",
            color: "#CBD5E1",
          }}
        >
          ✕
        </button>
        
        <div style={{ fontSize: "3rem", marginBottom: 4 }}>🎯</div>
        <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.8rem", color: "#1E293B" }}>
          Target Practice Complete!
        </div>
        <div style={{ fontWeight: 700, color: "#64748B", marginTop: 5, fontSize: "0.88rem" }}>
          Great timing skills!
        </div>
        
        <div style={{ display: "flex", gap: 12, justifyContent: "center", margin: "18px 0 20px" }}>
          {[
            { l: "Magic Points", v: magicPoints, bg: "#F0F9FF", c: "#0369A1" },
            { l: "Accuracy", v: `${accuracy}%`, bg: "#F0FDF4", c: "#16A34A" },
          ].map(({ l, v, bg, c }) => (
            <div key={l} style={{ flex: 1, background: bg, borderRadius: 16, padding: "12px 10px" }}>
              <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "1.7rem", color: c }}>{v}</div>
              <div
                style={{
                  fontSize: "0.62rem",
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: "0.12em",
                  color: c,
                  opacity: 0.6,
                  marginTop: 2,
                }}
              >
                {l}
              </div>
            </div>
          ))}
        </div>
        
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={goLevels}
            style={{
              padding: "14px 24px",
              borderRadius: 100,
              background: "linear-gradient(135deg,#7C3AED,#6D28D9)",
              color: "white",
              fontFamily: "'Fredoka One', cursive",
              fontSize: "1rem",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 7px 0 #5B21B6,0 9px 22px rgba(109,40,217,0.32)",
            }}
          >
            🐉 Continue Adventure
          </button>
          
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: "11px",
              borderRadius: 100,
              background: "white",
              border: "2px solid #E2E8F0",
              fontWeight: 800,
              fontSize: "0.82rem",
              cursor: "pointer",
              color: "#334155",
            }}
          >
            🎯 Play Again
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   MAIN GAME
═══════════════════════════════════════════════ */
export default function GameTargetPractice() {
  const go = useNavigate();
  const gameAreaRef = useRef(null);
  
  // Window width
  const [wW, setWW] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    let raf = 0;
    const fn = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWW(window.innerWidth));
    };
    window.addEventListener("resize", fn);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", fn);
    };
  }, []);
  
  const isMobile = wW < 640;
  
  // Piano sizing
  const pianoCfg = useMemo(() => {
    const kw = isMobile ? Math.floor((wW - 24) / 8) : 54;
    const r = kw / 54;
    return {
      kw,
      wh: Math.round(180 * r),
      bkw: Math.round(32 * r),
      bkh: Math.round(114 * r),
    };
  }, [isMobile, wW]);
  
  // Game state
  const [targets, setTargets] = useState([]);
  const [projectiles, setProjectiles] = useState([]);
  const [health, setHealth] = useState(MAX_HEALTH);
  const [magicPoints, setMagicPoints] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [targetsSpawned, setTargetsSpawned] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [showFinish, setShowFinish] = useState(false);
  const [listening, setListening] = useState(false);
  
  const nextTargetId = useRef(0);
  const nextProjectileId = useRef(0);
  
  // Game area dimensions
  const [gameAreaSize, setGameAreaSize] = useState({ width: 600, height: 400 });
  useEffect(() => {
    if (!gameAreaRef.current) return;
    const updateSize = () => {
      const rect = gameAreaRef.current?.getBoundingClientRect();
      if (rect) {
        setGameAreaSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);
  
  // Spawn targets
  useEffect(() => {
    if (!gameStarted || gameOver || targetsSpawned >= TOTAL_TARGETS) return;
    
    const spawnTarget = () => {
      setTargets((prev) => [...prev, createTarget(nextTargetId.current++)]);
      setTargetsSpawned((prev) => prev + 1);
    };
    
    // Spawn first target immediately
    if (targetsSpawned === 0) {
      spawnTarget();
    }
    
    // Spawn subsequent targets
    const interval = setInterval(() => {
      if (targetsSpawned < TOTAL_TARGETS - 1) {
        spawnTarget();
      }
    }, TARGET_SPAWN_INTERVAL);
    
    return () => clearInterval(interval);
  }, [gameStarted, gameOver, targetsSpawned]);
  
  // Update game state (remove old targets/projectiles, check collisions)
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      
      setTargets((prevTargets) => {
        const updated = prevTargets.filter((t) => {
          const elapsed = now - t.spawnTime;
          // Remove if fallen past bottom
          if (elapsed > TARGET_FALL_DURATION) {
            setHealth((h) => Math.max(0, h - 1));
            setMisses((m) => m + 1);
            return false;
          }
          return t.active;
        });
        return updated;
      });
      
      setProjectiles((prev) =>
        prev.filter((p) => {
          const elapsed = now - p.spawnTime;
          return elapsed < 400; // Remove after 400ms
        })
      );
    }, 50);
    
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);
  
  // Check collisions
  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    const interval = setInterval(() => {
      setProjectiles((prevProjectiles) => {
        const remainingProjectiles = [...prevProjectiles];
        
        setTargets((prevTargets) => {
          const remainingTargets = [...prevTargets];
          
          prevProjectiles.forEach((proj) => {
            const projElapsed = Date.now() - proj.spawnTime;
            const projProgress = Math.min(projElapsed / 400, 1);
            const projY = 1 - projProgress;
            
            prevTargets.forEach((target) => {
              if (!target.active) return;
              
              const targetElapsed = Date.now() - target.spawnTime;
              const targetProgress = Math.min(targetElapsed / TARGET_FALL_DURATION, 1);
              const sineOffset = Math.sin(targetElapsed / 800) * 0.3;
              const targetX = Math.max(0.1, Math.min(0.9, target.x + sineOffset));
              const targetY = targetProgress;
              
              // Simple distance check
              const dx = Math.abs(proj.x - targetX);
              const dy = Math.abs(projY - targetY);
              const distance = Math.sqrt(dx * dx + dy * dy);
              
              if (distance < 0.08) {
                // Hit!
                target.active = false;
                setMagicPoints((p) => p + 10);
                setHits((h) => h + 1);
                
                // Remove projectile
                const projIdx = remainingProjectiles.findIndex((p) => p.id === proj.id);
                if (projIdx !== -1) {
                  remainingProjectiles.splice(projIdx, 1);
                }
              }
            });
          });
          
          return remainingTargets.filter((t) => t.active);
        });
        
        return remainingProjectiles;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);
  
  // Check game over
  useEffect(() => {
    if (health <= 0 && !gameOver) {
      setGameOver(true);
      setShowFinish(true);
      try {
        const btn = document.querySelector(`#${MIC_ID} button`);
        if (btn) btn.click();
      } catch {}
      setListening(false);
    }
  }, [health, gameOver]);
  
  // Check game complete
  useEffect(() => {
    if (targetsSpawned >= TOTAL_TARGETS && targets.length === 0 && !gameOver) {
      setGameOver(true);
      setShowFinish(true);
      try {
        const btn = document.querySelector(`#${MIC_ID} button`);
        if (btn) btn.click();
      } catch {}
      setListening(false);
    }
  }, [targetsSpawned, targets.length, gameOver]);
  
  // Shoot projectile
  const shoot = useCallback((noteName) => {
    if (gameOver) return;
    
    const note = normAny(noteName);
    if (!note) return;
    
    // Find piano key position
    const keyEl = document.querySelector(`[data-note="${note}"]`);
    if (!keyEl || !gameAreaRef.current) return;
    
    const gameRect = gameAreaRef.current.getBoundingClientRect();
    const keyRect = keyEl.getBoundingClientRect();
    
    const x = (keyRect.left - gameRect.left + keyRect.width / 2) / gameRect.width;
    
    setProjectiles((prev) => [
      ...prev,
      {
        id: nextProjectileId.current++,
        note,
        x,
        spawnTime: Date.now(),
      },
    ]);
  }, [gameOver]);
  
  // Virtual piano click
  const onKey = useCallback(
    (noteName) => {
      if (!gameStarted) {
        setGameStarted(true);
      }
      shoot(noteName);
    },
    [gameStarted, shoot]
  );
  
  // Mic callbacks
  const onMicDetected = useCallback(
    (note) => {
      const pc = normAny(note);
      if (!pc || gameOver || !listening) return;
      shoot(pc);
    },
    [gameOver, listening, shoot]
  );
  
  // Toggle mic
  const toggleMic = useCallback(() => {
    const btn = document.querySelector(`#${MIC_ID} button`);
    if (btn) {
      btn.click();
      setListening((p) => !p);
    }
  }, []);
  
  // Reset game
  const reset = useCallback(() => {
    if (listening) {
      try {
        const btn = document.querySelector(`#${MIC_ID} button`);
        if (btn) btn.click();
      } catch {}
    }
    setListening(false);
    setTargets([]);
    setProjectiles([]);
    setHealth(MAX_HEALTH);
    setMagicPoints(0);
    setHits(0);
    setMisses(0);
    setTargetsSpawned(0);
    setGameStarted(false);
    setGameOver(false);
    setShowFinish(false);
    nextTargetId.current = 0;
    nextProjectileId.current = 0;
  }, [listening]);
  
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: "linear-gradient(155deg,#FFF7ED 0%,#EFF6FF 55%,#F0FDF4 100%)",
        fontFamily: "'Nunito',sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');
        @keyframes gtpPop { from{transform:scale(0.5);opacity:0} to{transform:scale(1);opacity:1} }
        button { -webkit-tap-highlight-color:transparent; }
      `}</style>
      
      {/* NAV */}
      <header
        style={{
          position: "relative",
          zIndex: 100,
          background: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.07)",
          flexShrink: 0,
          height: 46,
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            width: "100%",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button
            type="button"
            onClick={() => go("/home")}
            style={{
              fontFamily: "'Fredoka One', cursive",
              fontSize: "1.2rem",
              color: "#1E40AF",
              background: "none",
              border: "none",
              cursor: "pointer",
            }}
          >
            Piano With Pan
          </button>
          
          <div
            style={{
              fontFamily: "'Fredoka One', cursive",
              fontSize: isMobile ? "0.85rem" : "1.05rem",
              color: "#64748B",
            }}
          >
            🎯 Target Practice
          </div>
          
          <button
            type="button"
            onClick={() => go("/game-menu")}
            style={{
              background: "none",
              border: "1.5px solid #cbd5e1",
              borderRadius: 100,
              padding: "4px 14px",
              fontSize: "0.72rem",
              fontWeight: 900,
              color: "#334155",
              cursor: "pointer",
            }}
          >
            Menu
          </button>
        </div>
      </header>
      
      {/* GAME AREA */}
      <div style={{ flex: 1, position: "relative", padding: "16px", display: "flex", flexDirection: "column" }}>
        {/* HUD */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <HealthBar current={health} max={MAX_HEALTH} />
          
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
            }}
          >
            <StatCard emoji="⭐" label="Magic" value={magicPoints} color="#8B5CF6" />
            <StatCard emoji="🎯" label="Targets" value={`${targetsSpawned}/${TOTAL_TARGETS}`} color="#3B82F6" />
          </div>
        </div>
        
        {/* Game arena */}
        <div
          ref={gameAreaRef}
          style={{
            flex: 1,
            position: "relative",
            background: "rgba(255,255,255,0.5)",
            borderRadius: 20,
            marginBottom: 16,
            minHeight: 300,
            overflow: "hidden",
          }}
        >
          {/* Render targets */}
          {targets.map((target) => (
            <FallingTarget
              key={target.id}
              target={target}
              gameAreaWidth={gameAreaSize.width}
              gameAreaHeight={gameAreaSize.height}
            />
          ))}
          
          {/* Render projectiles */}
          {projectiles.map((proj) => (
            <Projectile
              key={proj.id}
              projectile={proj}
              gameAreaWidth={gameAreaSize.width}
              gameAreaHeight={gameAreaSize.height}
            />
          ))}
          
          {/* Start prompt */}
          {!gameStarted && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(0,0,0,0.3)",
                backdropFilter: "blur(4px)",
              }}
            >
              <div style={{ textAlign: "center", color: "white" }}>
                <div style={{ fontFamily: "'Fredoka One', cursive", fontSize: "2rem", marginBottom: 8 }}>
                  🎯 Ready?
                </div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700 }}>
                  Click any piano key to start!
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Mic button */}
        {gameStarted && !gameOver && (
          <div style={{ marginBottom: 10, display: "flex", justifyContent: "center" }}>
            <button
              type="button"
              onClick={toggleMic}
              style={{
                padding: "8px 22px",
                borderRadius: 100,
                background: listening ? "#ECFDF5" : "rgba(255,255,255,0.9)",
                border: `2px solid ${listening ? "#86EFAC" : "#E2E8F0"}`,
                color: listening ? "#166534" : "#64748B",
                fontWeight: 800,
                fontSize: "0.78rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 7,
                transition: "all .2s",
              }}
            >
              {listening ? "🎤 Listening…" : "🎤 Enable Mic"}
            </button>
          </div>
        )}
        
        {/* Piano */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            background: "rgba(255,255,255,0.80)",
            borderRadius: 24,
            padding: `16px ${isMobile ? 12 : 28}px 20px`,
            boxShadow: "0 14px 45px rgba(0,0,0,0.07),inset 0 1px 0 rgba(255,255,255,0.9)",
            border: "1.5px solid rgba(255,255,255,0.9)",
          }}
        >
          <VirtualPiano
            onNoteClick={onKey}
            keyWidth={pianoCfg.kw}
            whiteKeyHeight={pianoCfg.wh}
            blackKeyWidth={pianoCfg.bkw}
            blackKeyHeight={pianoCfg.bkh}
            firstKey="C4"
            lastKey="C5"
            showAllBlackLabels={false}
          />
        </div>
        
        {/* Bottom links */}
        <div style={{ display: "flex", gap: 22, justifyContent: "center", marginTop: 10 }}>
          <button
            type="button"
            onClick={reset}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontWeight: 800,
              fontSize: "0.72rem",
              color: "#94A3B8",
              textDecoration: "underline",
              textDecorationStyle: "dashed",
              textUnderlineOffset: 3,
            }}
          >
            Restart
          </button>
        </div>
      </div>
      
      {/* Hidden mic */}
      <div id={MIC_ID} style={{ display: "none" }}>
        <RealPianoListener
          targetMelody={WHITE_NOTES}
          startLabel=" "
          stopLabel=" "
          showMicHint={false}
          buttonWrapperClassName="hidden"
          containerClassName="hidden"
          onNoteDetected={onMicDetected}
          detectionSensitivity={MIC_CFG.detectionSensitivity}
          toleranceCents={MIC_CFG.toleranceCents}
          toleranceHz={MIC_CFG.toleranceHz}
        />
      </div>
      
      <FinishModal
        open={showFinish}
        magicPoints={magicPoints}
        hits={hits}
        misses={misses}
        onClose={() => setShowFinish(false)}
        onRetry={reset}
        goLevels={() => go("/game-menu")}
      />
    </div>
  );
}